import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CurrencyEur,
  CheckCircle,
  XCircle,
  FilePdf,
  Plus,
  TrashSimple,
  PencilSimple,
  CaretDown,
  CaretUp,
  WhatsappLogo,
  Truck as TruckIcon,
  Camera,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { buildWaLink, buildSanctionMessage } from "@/lib/whatsapp";
import { useSites } from "@/lib/sites";
import { useAuth, filterSitesForUser, getUserAllowedSites } from "@/lib/auth";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { photoUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  fetchPenaltiesMonthly,
  createPenalty,
  updatePenalty,
  deletePenalty,
} from "@/lib/api";

function todayMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function frenchMonth(iso) {
  const [y, m] = iso.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

function frenchDate(iso) {
  try {
    const [y, m, d] = iso.split("-");
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
}

export default function PrimesPage() {
  const { sites: allSitesList } = useSites();
  const { user } = useAuth();
  const sitesList = filterSitesForUser(allSitesList, user);
  const userScope = getUserAllowedSites(user);
  const defaultTab = userScope && userScope.length === 1 ? userScope[0] : "ALL";
  const [month, setMonth] = useState(todayMonthISO());
  const [siteTab, setSiteTab] = useState(defaultTab);
  const [data, setData] = useState({ drivers: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | eligible | sanctioned
  const [expanded, setExpanded] = useState({}); // driver name → bool
  const [creatingFor, setCreatingFor] = useState(null); // driver name when adding manual penalty
  const [lightbox, setLightbox] = useState({ list: [], index: null });
  const openLightbox = (list, index) => setLightbox({ list, index });
  const closeLightbox = () => setLightbox({ list: [], index: null });
  const prevLightbox = () =>
    setLightbox((lb) => (lb.index == null ? lb : { ...lb, index: (lb.index - 1 + lb.list.length) % lb.list.length }));
  const nextLightbox = () =>
    setLightbox((lb) => (lb.index == null ? lb : { ...lb, index: (lb.index + 1) % lb.list.length }));

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchPenaltiesMonthly(month, siteTab === "ALL" ? null : siteTab);
      setData(res);
    } catch {
      if (!silent) toast.error("Erreur de chargement");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [month, siteTab]);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const rows = data.drivers.filter((d) => {
      if (s && !d.name.toLowerCase().includes(s)) return false;
      if (statusFilter === "eligible" && !d.eligible) return false;
      if (statusFilter === "sanctioned" && d.eligible) return false;
      return true;
    });
    // Sort: sanctionnés first, then by most recent penalty creation date
    // (fallback: start_date, then alphabetical for eligible drivers).
    const mostRecentDate = (d) => {
      const pool = [...(d.active_penalties || []), ...(d.future_penalties || [])];
      if (pool.length === 0) return "";
      const dates = pool.map((p) => p.created_at || p.start_date || "");
      return dates.sort().reverse()[0];
    };
    rows.sort((a, b) => {
      if (a.eligible !== b.eligible) return a.eligible ? 1 : -1;
      if (!a.eligible) {
        // Both sanctionnés → most recent sanction date first
        const dA = mostRecentDate(a);
        const dB = mostRecentDate(b);
        if (dA !== dB) return dA < dB ? 1 : -1;
      }
      return a.name.localeCompare(b.name, "fr");
    });
    return rows;
  }, [data.drivers, search, statusFilter]);

  const exportPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    // Header
    doc.setFillColor(15, 15, 15);
    doc.rect(0, 0, pageW, 22, "F");
    doc.setFillColor(212, 175, 55);
    doc.rect(0, 22, pageW, 1.5, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("NY TRANS", 14, 10);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(212, 175, 55);
    doc.text(`PRIMES MENSUELLES · ${frenchMonth(month).toUpperCase()}`, 14, 16);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(new Date().toLocaleDateString("fr-FR").toUpperCase(), pageW - 14, 12, { align: "right" });

    // Summary line
    const s = data.summary || {};
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${s.total_drivers || 0} chauffeurs · ${s.eligible || 0} éligibles · ${s.sanctioned || 0} sanctionnés · Économie : ${(s.total_saved || 0).toFixed(2)} €`,
      14,
      32
    );

    // Table — sanctioned first, then eligible
    const sorted = [...filtered].sort((a, b) => {
      if (a.eligible === b.eligible) return a.name.localeCompare(b.name);
      return a.eligible ? 1 : -1;
    });

    autoTable(doc, {
      startY: 38,
      head: [["#", "Chauffeur", "Statut", "Détail sanction", "Fin"]],
      body: sorted.map((d, i) => {
        if (d.eligible) {
          return [String(i + 1), d.name, "ÉLIGIBLE", "—", "—"];
        }
        const p = d.active_penalties[0];
        const others = d.active_penalties.length > 1 ? ` (+${d.active_penalties.length - 1})` : "";
        return [
          String(i + 1),
          d.name,
          "SANCTIONNÉ",
          `${p.months}m · ${p.reason || "—"}${others}`,
          frenchDate(p.end_date),
        ];
      }),
      theme: "grid",
      headStyles: { fillColor: [15, 15, 15], textColor: [212, 175, 55], fontStyle: "bold", fontSize: 9, cellPadding: 2.5 },
      bodyStyles: { fontSize: 9, cellPadding: 2.2, textColor: [25, 25, 25] },
      alternateRowStyles: { fillColor: [248, 246, 240] },
      columnStyles: {
        0: { cellWidth: 10, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 60 },
        2: { cellWidth: 28, fontStyle: "bold", halign: "center" },
        4: { cellWidth: 28, halign: "center" },
      },
      didParseCell: (d2) => {
        if (d2.section === "body" && d2.column.index === 2) {
          if (d2.cell.raw === "ÉLIGIBLE") {
            d2.cell.styles.textColor = [10, 100, 50];
          } else {
            d2.cell.styles.textColor = [200, 40, 40];
          }
        }
      },
    });

    const finalY = doc.lastAutoTable.finalY || 50;
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.4);
    doc.line(14, finalY + 8, pageW - 14, finalY + 8);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.text(`Document généré le ${new Date().toLocaleString("fr-FR")} · NY Trans · Primes mensuelles`, 14, finalY + 14);

    doc.save(`primes-${siteTab}-${month}.pdf`);
    toast.success("PDF exporté");
  };

  const handleDeletePenalty = async (id) => {
    if (!window.confirm("Annuler définitivement cette sanction ?")) return;
    try {
      await deletePenalty(id);
      toast.success("Sanction supprimée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid="primes-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">NY Trans</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-1">
            <CurrencyEur size={28} weight="bold" className="inline mr-2 -mt-1 text-[var(--ny-gold)]" />
            Primes mensuelles
          </h1>
          <div className="ny-divider w-32 mt-2" />
          <p className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">
            Qui touche sa prime ce mois-ci · Qui est sanctionné · Calcul automatique
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="label-tag">Mois</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="rounded-none border-zinc-900 w-44"
              data-testid="primes-month"
            />
          </div>
          <button
            onClick={exportPDF}
            disabled={filtered.length === 0}
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-[var(--ny-gold)] text-zinc-900 hover:bg-[var(--ny-gold-deep)] hover:text-white disabled:opacity-40 inline-flex items-center gap-2 h-10"
            data-testid="primes-pdf"
          >
            <FilePdf size={14} weight="bold" /> Exporter PDF
          </button>
        </div>
      </div>

      {/* Site tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-zinc-900 flex-wrap" data-testid="primes-site-tabs">
        {[
          ...sitesList.map((s) => ({ key: s.code, label: s.name ? `${s.code} · ${s.name}` : s.code })),
          ...(sitesList.length > 1 ? [{ key: "ALL", label: "Tous" }] : []),
        ].map((s) => {
          const active = siteTab === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSiteTab(s.key)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border border-b-0 ${
                active
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
              }`}
              data-testid={`primes-tab-${s.key}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="primes-summary">
        <SumCard label="Total chauffeurs" value={data.summary?.total_drivers ?? "—"} tone="default" />
        <SumCard label="Éligibles" value={data.summary?.eligible ?? 0} tone="eligible" sub="touchent la prime" onClick={() => setStatusFilter("eligible")} />
        <SumCard label="Sanctionnés" value={data.summary?.sanctioned ?? 0} tone="sanctioned" sub="prime suspendue" onClick={() => setStatusFilter("sanctioned")} />
        <SumCard label="Économie" value={`${(data.summary?.total_saved ?? 0).toFixed(0)} €`} tone="gold" sub="ce mois-ci" />
      </div>

      {/* Filters */}
      <div className="border border-zinc-900 bg-white mb-4 px-3 py-2 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un chauffeur…"
          className="rounded-none border-zinc-300 h-9 text-sm flex-1 min-w-[200px]"
          data-testid="primes-search"
        />
        <div className="inline-flex border border-zinc-300">
          {[
            { key: "all", label: "Tous" },
            { key: "eligible", label: "Éligibles" },
            { key: "sanctioned", label: "Sanctionnés" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${
                statusFilter === f.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid={`primes-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 mono">
          {filtered.length} / {data.drivers.length} chauffeur(s)
        </div>
      </div>

      {/* List */}
      <div className="border border-zinc-900 bg-white">
        {loading && (
          <div className="text-center text-xs text-zinc-500 py-6 uppercase tracking-widest">Chargement…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-xs text-zinc-500 py-10">Aucun chauffeur ne correspond aux filtres.</div>
        )}
        {!loading && filtered.map((d) => {
          const isOpen = expanded[d.name];
          return (
            <div key={d.name} className="border-b border-zinc-200 last:border-b-0" data-testid={`prime-row-${d.name}`}>
              <div
                className={`flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 cursor-pointer ${
                  d.eligible ? "" : "bg-red-50"
                }`}
                onClick={() => setExpanded((p) => ({ ...p, [d.name]: !p[d.name] }))}
              >
                <span className="text-zinc-400 text-xs mono w-6">
                  {isOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                </span>
                {d.eligible ? (
                  <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                ) : (
                  <XCircle size={18} weight="fill" className="text-red-600" />
                )}
                <span className="font-bold uppercase tracking-tight flex-1">{d.name}</span>
                <span
                  className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                    d.eligible ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
                  }`}
                  data-testid={`prime-status-${d.name}`}
                >
                  {d.eligible ? "ÉLIGIBLE" : "SANCTIONNÉ"}
                </span>
                {!d.eligible && d.active_penalties[0] && (
                  <span className="text-[10px] mono text-zinc-600">
                    jusqu&apos;au {frenchDate(d.active_penalties[0].end_date)}
                  </span>
                )}
                {(d.future_penalties || []).length > 0 && (
                  <span
                    className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-amber-500 text-white inline-flex items-center gap-1"
                    title={`Cette personne a ${d.future_penalties.length} sanction(s) programmée(s) pour les mois à venir. Cliquez pour voir le détail.`}
                    data-testid={`prime-future-count-${d.name}`}
                  >
                    +{d.future_penalties.length} à venir
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-widest text-zinc-400">
                  {d.history_count}× historique
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreatingFor(d.name);
                  }}
                  className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-1"
                  data-testid={`prime-add-${d.name}`}
                >
                  <Plus size={11} weight="bold" /> Sanction
                </button>
                {d.phone && d.active_penalties && d.active_penalties[0] && (
                  <a
                    href={buildWaLink(d.phone, buildSanctionMessage({
                      driverName: d.name,
                      months: d.active_penalties[0].months,
                      monthlyAmount: d.active_penalties[0].monthly_amount,
                      reason: d.active_penalties[0].reason,
                    }))}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-emerald-300 text-emerald-700 hover:border-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                    title={`Prévenir ${d.name} sur WhatsApp`}
                    data-testid={`prime-whatsapp-${d.name}`}
                  >
                    <WhatsappLogo size={11} weight="bold" /> WhatsApp
                  </a>
                )}
              </div>
              {isOpen && (
                <div className="bg-zinc-50 px-12 py-3 border-t border-zinc-200">
                  {d.active_penalties.length === 0 && (d.future_penalties || []).length === 0 && (
                    <div className="text-xs text-zinc-500">Aucune sanction active pour ce mois.</div>
                  )}
                  {d.active_penalties.length > 0 && (
                    <>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1">
                        Sanctions actives ({d.active_penalties.length})
                      </div>
                      {d.active_penalties.map((p) => (
                        <PenaltyRow
                          key={p.id}
                          penalty={p}
                          onChange={load}
                          onDelete={handleDeletePenalty}
                          onOpenLightbox={openLightbox}
                        />
                      ))}
                    </>
                  )}
                  {(d.future_penalties || []).length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1 inline-flex items-center gap-1">
                        Sanctions programmées à venir ({d.future_penalties.length})
                      </div>
                      {d.future_penalties.map((p) => (
                        <PenaltyRow
                          key={p.id}
                          penalty={p}
                          future
                          onChange={load}
                          onDelete={handleDeletePenalty}
                          onOpenLightbox={openLightbox}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {creatingFor && (
        <CreatePenaltyDialog
          driverName={creatingFor}
          onClose={() => setCreatingFor(null)}
          onCreated={() => {
            setCreatingFor(null);
            load();
          }}
        />
      )}

      <PhotoLightbox
        photos={lightbox.list}
        index={lightbox.index}
        onClose={closeLightbox}
        onPrev={prevLightbox}
        onNext={nextLightbox}
      />
    </div>
  );
}

function PenaltyRow({ penalty, onChange, onDelete, onOpenLightbox, future = false }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [months, setMonths] = useState(penalty.months);
  const [amount, setAmount] = useState(penalty.monthly_amount);
  const [reason, setReason] = useState(penalty.reason || "");
  const [saving, setSaving] = useState(false);
  const impactPhotos = Array.isArray(penalty.impact_photos) ? penalty.impact_photos : [];

  const save = async () => {
    setSaving(true);
    try {
      await updatePenalty(penalty.id, {
        months: parseInt(months) || 1,
        monthly_amount: parseFloat(amount) || 0,
        reason: reason.trim(),
      });
      toast.success("Sanction mise à jour");
      setEditing(false);
      onChange && onChange();
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="bg-white border border-zinc-300 px-3 py-2 mb-2">
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div>
            <Label className="label-tag">Durée (mois)</Label>
            <Input type="number" min="1" value={months} onChange={(e) => setMonths(e.target.value)} className="rounded-none border-zinc-300 h-8" />
          </div>
          <div>
            <Label className="label-tag">Montant / mois (€)</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-none border-zinc-300 h-8" />
          </div>
          <div>
            <Label className="label-tag">Motif</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none border-zinc-300 h-8" />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={() => setEditing(false)} className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300">Annuler</button>
          <button onClick={save} disabled={saving} className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-900 text-white">
            {saving ? "Sauvegarde…" : "Enregistrer"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border mb-1.5 ${
        future ? "bg-amber-50 border-amber-300" : "bg-white border-zinc-200"
      }`}
      data-testid={`penalty-${penalty.id}`}
    >
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs">
      {future && (
        <span
          className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white"
          title="Sanction programmée pour un mois futur"
        >
          À venir
        </span>
      )}
      <span className="mono uppercase font-bold text-red-700">{penalty.months}m</span>
      <span className="text-zinc-700">{penalty.reason || "— sans motif —"}</span>
      <span className="text-zinc-400 mono">
        {penalty.start_date} → {penalty.end_date}
      </span>
      {penalty.monthly_amount > 0 && (
        <span className="font-bold text-amber-700 mono">{penalty.monthly_amount.toFixed(2)} €/mois</span>
      )}
      {penalty.truck_id && penalty.truck_plate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/trucks/${penalty.truck_id}`);
          }}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 text-zinc-800 hover:border-zinc-900 hover:bg-zinc-50 mono"
          title={`Ouvrir la fiche du camion ${penalty.truck_plate}`}
          data-testid={`penalty-truck-${penalty.id}`}
        >
          <TruckIcon size={11} weight="bold" /> {penalty.truck_plate}
        </button>
      )}
      <div className="ml-auto inline-flex gap-1">
        <button onClick={() => setEditing(true)} className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900" data-testid={`penalty-edit-${penalty.id}`}>
          <PencilSimple size={11} weight="bold" />
        </button>
        <button onClick={() => onDelete(penalty.id)} className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-red-600 hover:text-red-600" data-testid={`penalty-delete-${penalty.id}`}>
          <TrashSimple size={11} weight="bold" />
        </button>
      </div>
      </div>
      {impactPhotos.length > 0 && (
        <div
          className="border-t border-zinc-200 px-3 py-2 bg-zinc-50/60"
          data-testid={`penalty-impact-photos-${penalty.id}`}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <Camera size={12} weight="bold" className="text-zinc-500" />
            <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-600">
              Photos de l&apos;impact ({impactPhotos.length})
            </span>
            {penalty.impact_severity && (
              <span
                className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
                  penalty.impact_severity === "critique"
                    ? "bg-red-600 text-white"
                    : penalty.impact_severity === "majeur"
                    ? "bg-orange-500 text-white"
                    : "bg-amber-400 text-zinc-900"
                }`}
                title={`Gravité déclarée dans l'impact lié`}
              >
                {penalty.impact_severity}
              </span>
            )}
            {penalty.impact_date && (
              <span className="text-[10px] mono text-zinc-500">
                {frenchDate(penalty.impact_date.slice(0, 10))}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {impactPhotos.map((ph, idx) => (
              <button
                key={`${penalty.id}-ph-${idx}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenLightbox && onOpenLightbox(impactPhotos, idx);
                }}
                className="relative w-16 h-16 border border-zinc-300 hover:border-zinc-900 overflow-hidden group"
                title="Voir en grand"
                data-testid={`penalty-photo-${penalty.id}-${idx}`}
              >
                <img
                  src={photoUrl(ph)}
                  alt={`impact-${idx + 1}`}
                  className="w-full h-full object-cover group-hover:opacity-90"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CreatePenaltyDialog({ driverName, onClose, onCreated }) {
  const [months, setMonths] = useState(1);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [stack, setStack] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!months || months < 1) {
      toast.error("Durée minimum : 1 mois");
      return;
    }
    setSaving(true);
    try {
      await createPenalty({
        driver_name: driverName,
        months: parseInt(months),
        monthly_amount: parseFloat(amount) || 0,
        reason: reason.trim(),
        stack,
      });
      toast.success(`Sanction créée pour ${driverName}`);
      onCreated && onCreated();
    } catch {
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border border-zinc-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black uppercase tracking-tight" style={{ fontFamily: "Chivo" }}>
            Sanction prime
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Chauffeur : <span className="font-bold text-zinc-900">{driverName}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-tag">Durée *</Label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 6].map((n) => (
                <button
                  key={n}
                  onClick={() => setMonths(n)}
                  className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest border ${
                    months === n ? "bg-red-600 border-red-600 text-white" : "bg-white border-zinc-300 hover:border-red-600"
                  }`}
                  data-testid={`penalty-months-${n}`}
                >
                  {n} mois
                </button>
              ))}
            </div>
            <Input
              type="number"
              min="1"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              className="rounded-none border-zinc-300 mt-2"
              placeholder="Custom"
              data-testid="penalty-months-custom"
            />
          </div>
          <div>
            <Label className="label-tag">Montant / mois (€)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-none border-zinc-300"
              placeholder="Ex: 120"
              data-testid="penalty-amount"
            />
          </div>
          <div>
            <Label className="label-tag">Motif</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="rounded-none border-zinc-300"
              placeholder="Ex: Choc latéral, retard chronique…"
              data-testid="penalty-reason"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stack}
              onChange={(e) => setStack(e.target.checked)}
              data-testid="penalty-stack"
            />
            <span className="text-xs uppercase tracking-widest">
              Cumuler après les sanctions en cours
            </span>
          </label>
          <div className="border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800 leading-relaxed">
            {stack ? (
              <>
                <span className="font-bold">Mode « cumul » :</span> si le chauffeur a
                déjà une sanction en cours, cette nouvelle sanction commencera
                <strong> le mois suivant la fin</strong> de la dernière. Elle apparaîtra
                alors dans l&apos;onglet du mois concerné (et dans la section
                « Sanctions à venir » pour le mois actuel).
              </>
            ) : (
              <>
                <span className="font-bold">Mode « immédiat » :</span> la sanction
                commence le <strong>1er du mois en cours</strong> et se cumule
                <strong> en parallèle</strong> avec les sanctions existantes (elles
                s&apos;additionnent sur le montant total à retenir).
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-zinc-900" data-testid="penalty-cancel-btn">
            Annuler
          </Button>
          <Button onClick={submit} disabled={saving} className="rounded-none bg-red-600 text-white hover:bg-red-700" data-testid="penalty-save-btn">
            {saving ? "Création…" : "Créer la sanction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SumCard({ label, value, sub, tone = "default", onClick }) {
  const cls =
    tone === "eligible"
      ? "border-emerald-600 bg-emerald-50 text-emerald-900"
      : tone === "sanctioned"
      ? "border-red-600 bg-red-50 text-red-900"
      : tone === "gold"
      ? "border-zinc-900 bg-[var(--ny-gold)] text-zinc-900"
      : "border-zinc-300 bg-white text-zinc-900";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`text-left border ${cls} px-3 py-3 ${
        onClick ? "hover:ring-2 hover:ring-offset-1 hover:ring-[var(--ny-gold)] cursor-pointer" : "cursor-default"
      }`}
    >
      <div className="label-tag !text-current opacity-70">{label}</div>
      <div className="text-3xl font-black mono mt-1 leading-none">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest opacity-70 mt-1">{sub}</div>}
    </button>
  );
}
